import { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CalendarToday,
  Event as EventIcon,
} from '@mui/icons-material';
import { useSchedule } from '@hooks/useSchedule';
import ScheduleEventCard from '@components/schedule/ScheduleEventCard';

const SchedulePage = () => {
  const [tabValue, setTabValue] = useState(0);
  const { upcomingEvents, pastEvents, loading, error, refresh } = useSchedule();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    refresh();
  };

  // Empty state component
  const EmptyState = ({ message, icon: Icon }) => (
    <Paper
      elevation={0}
      sx={{
        p: 8,
        textAlign: 'center',
        bgcolor: 'grey.50',
        borderRadius: 2,
      }}
    >
      <Icon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
      <Typography variant="h6" color="text.secondary">
        {message}
      </Typography>
    </Paper>
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            My Schedule
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            View and manage your event registrations
          </Typography>
        </Box>

        <IconButton
          onClick={handleRefresh}
          disabled={loading}
          sx={{
            bgcolor: 'primary.lighter',
            '&:hover': { bgcolor: 'primary.light' },
            minWidth: 48,
            minHeight: 48,
          }}
          aria-label="Refresh schedule"
        >
          {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
        </IconButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => refresh()}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' },
            },
          }}
        >
          <Tab
            label={`Upcoming (${upcomingEvents.length})`}
            icon={<CalendarToday />}
            iconPosition="start"
          />
          <Tab
            label={`Past (${pastEvents.length})`}
            icon={<EventIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Loading State */}
      {loading && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Tab Panels */}
      {!loading && (
        <>
          {/* Upcoming Events Tab */}
          {tabValue === 0 && (
            <Box>
              {upcomingEvents.length > 0 ? (
                upcomingEvents.map((registration) => (
                  <ScheduleEventCard
                    key={registration.registrationId}
                    registration={registration}
                    onUpdate={refresh}
                  />
                ))
              ) : (
                <EmptyState
                  message="You have no upcoming events. Browse events to register!"
                  icon={CalendarToday}
                />
              )}
            </Box>
          )}

          {/* Past Events Tab */}
          {tabValue === 1 && (
            <Box>
              {pastEvents.length > 0 ? (
                pastEvents.map((registration) => (
                  <ScheduleEventCard
                    key={registration.registrationId}
                    registration={registration}
                    onUpdate={refresh}
                  />
                ))
              ) : (
                <EmptyState
                  message="You have no past events yet. Your completed events will appear here."
                  icon={EventIcon}
                />
              )}
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default SchedulePage;
